<script setup lang="ts">
/**
 * Fullmoonlit presentation of AIRI's own Chat and Chat Session.
 * This is not another Chat engine, history store or provider/voice pipeline.
 * The R1 account-wide lease and elected local Leader bound every new turn.
 */
import { ChatHistory, useChatComposer } from '@proj-airi/stage-ui/components/scenarios/chat'
import { usePiniaSynced } from '@proj-airi/stage-ui/libs/pinia'
import { useAuthStore } from '@proj-airi/stage-ui/stores/auth'
import { useChatStore } from '@proj-airi/stage-ui/stores/chat'
import { useChatSessionStore } from '@proj-airi/stage-ui/stores/chat/session-store'
import { useChatStreamStore } from '@proj-airi/stage-ui/stores/chat/stream-store'
import { useAiriCardStore } from '@proj-airi/stage-ui/stores/modules/airi-card'
import { useConsciousnessStore } from '@proj-airi/stage-ui/stores/modules/consciousness'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import { useHoloRuntimeAuthorityStore } from '../holo/holo-runtime-authority-store'

const auth = useAuthStore()
const cards = useAiriCardStore()
const consciousness = useConsciousnessStore()
const authority = useHoloRuntimeAuthorityStore()
const chat = useChatStore()
const sessions = useChatSessionStore()
const stream = useChatStreamStore()
const synced = usePiniaSynced()

const { activeSessionId, sessionMetas, messages } = storeToRefs(sessions)
const { activeTurns } = storeToRefs(chat)
const { streamingMessage } = storeToRefs(stream)
const isLeader = ref(synced.isLeader())
const isForeground = ref(document.visibilityState === 'visible')
const pending = ref(false)
const failure = ref<string | null>(null)
const currentSession = computed(() => sessionMetas.value[activeSessionId.value])
const generating = computed(() => activeTurns.value.some(turn => turn.sessionId === activeSessionId.value))

const canSend = computed(() => {
  const lease = authority.runtimeLease
  const meta = currentSession.value
  return auth.isAuthenticated
    && consciousness.chatReady
    && authority.isActive
    && synced.isLeader()
    && isLeader.value
    && isForeground.value
    && !!lease
    && lease.user_id === auth.userId
    && lease.client_runtime_id === authority.clientRuntimeId
    && lease.character_id === cards.activeCardId
    && lease.expires_at > Date.now()
    && !!meta
    && meta.sessionId === activeSessionId.value
    && meta.userId === auth.userId
    && meta.characterId === lease.character_id
})

const composer = useChatComposer({
  activeSessionId,
  // Delegate ALL message generation/history/provider/ACT to upstream ChatStore.
  send: async (submission) => {
    if (!canSend.value || currentSession.value?.sessionId !== submission.sessionId) {
      failure.value = '当前终端或会话没有角色对话运行权。'
      throw new Error('holo_character_runtime_not_active')
    }
    try {
      await chat.send({
        sessionId: submission.sessionId,
        text: submission.text,
        replyToMessageId: submission.replyToMessageId,
      })
    }
    catch (error) {
      failure.value = error instanceof Error ? error.message : String(error)
      throw error
    }
  },
})
const messageInput = composer.draft

async function submitMessage() {
  if (pending.value || !canSend.value || !messageInput.value.trim())
    return
  pending.value = true
  failure.value = null
  try {
    const result = await composer.submit({
      beforeSend: async submission => {
        // A lease/Character/account handoff while the composer snapshots its
        // draft must never start a provider request on the old session.
        if (!canSend.value || currentSession.value?.sessionId !== submission.sessionId) {
          failure.value = '角色运行权已发生变化，请重新确认当前终端。'
          throw new Error('holo_character_runtime_not_active')
        }
      },
    })
    if (result === 'restored' && !failure.value)
      failure.value = '对话暂未发送，草稿已恢复。'
  }
  finally {
    pending.value = false
  }
}

function updateVisibility() {
  isForeground.value = document.visibilityState === 'visible'
}

let offLeader: (() => void) | undefined
onMounted(() => {
  offLeader = synced.onLeadershipChange(value => {
    isLeader.value = value
  })
  document.addEventListener('visibilitychange', updateVisibility)
})
onUnmounted(() => {
  offLeader?.()
  document.removeEventListener('visibilitychange', updateVisibility)
})

// A local draft belongs to its account and Character, never the next user
// of this browser. Native AIRI alone owns persisted chat messages/sessions.
watch([() => auth.userId, () => cards.activeCardId], () => {
  messageInput.value = ''
  composer.clearReply()
  failure.value = null
})
</script>

<template>
  <section class="conversation" aria-label="AIRI 原生聊天">
    <div class="section-heading">
      <div>
        <p class="eyebrow">AIRI 原生 Chat</p>
        <h2>与角色对话</h2>
      </div>
      <span class="status-pill">{{ canSend ? '可以发送' : '只读' }}</span>
    </div>

    <p class="conversation-hint" data-testid="stage-holo-native-chat-provider-status">
      AIRI 原生对话模型：{{ consciousness.activeProvider || '未配置' }} /
      {{ consciousness.activeModel || '未选择' }}
      <span v-if="!consciousness.chatReady">（需先配置 AIRI Provider 与模型）</span>
    </p>
    <p v-if="!canSend" class="conversation-hint" role="status">
      仅 ACTIVE 的本地 Leader 窗口可以发起对话；其他终端可阅读当前账号的历史。
    </p>
    <p v-else class="conversation-hint">
      消息通过 AIRI ChatStore 和当前 Character Session 发送；需要已配置的原生 LLM Provider。
    </p>

    <div class="conversation-feed" data-testid="stage-holo-native-chat-history">
      <ChatHistory
        :messages="messages"
        :sending="generating"
        :streaming-message="streamingMessage"
        surface="opaque"
        variant="desktop"
        class="conversation-history"
      />
    </div>

    <form class="conversation-compose" @submit.prevent="submitMessage">
      <label for="holo-native-message">消息输入</label>
      <textarea
        id="holo-native-message"
        v-model="messageInput"
        aria-label="消息输入"
        rows="3"
        :disabled="!canSend || pending"
        placeholder="给角色发送消息…"
      />
      <div class="conversation-actions">
        <p v-if="failure" role="alert" class="error-text">{{ failure }}</p>
        <button type="submit" :disabled="!canSend || pending || !messageInput.trim()">
          {{ pending ? '正在发送…' : '发送消息' }}
        </button>
      </div>
    </form>
  </section>
</template>
